import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { readUrlParam, useUrlSync } from '../shared/urlState';

type Locale = 'hr' | 'en';
type Status = 'ok' | 'warn' | 'error' | 'na';

type Metric = { label: string; value: number; is_count?: boolean };
type Control = {
    id: string;
    name_hr: string;
    name_en: string;
    hsfi: string;
    status: Status;
    materiality_eur: number;
    headline_hr: string;
    headline_en: string;
    metrics: Metric[];
    has_detail: boolean;
};
type Company = { id: number; code: string; name: string };
type Overview = {
    company: Company;
    year: number;
    years: number[];
    health_score: number;
    summary: { controls: number; ok: number; warn: number; error: number };
    headlines: Array<{ status: Status; hr: string; en: string; eur: number }>;
    cross_year_alerts: Array<{ year: number; diff: number }>;
    controls: Control[];
    generated_at: string;
};
type ControlDetail = {
    key: string;
    explain_hr: string;
    columns: string[];
    rows: Array<Record<string, string | number | null>>;
    missing_salary_months?: number[];
    missing_depr_months?: number[];
};
type Statements = {
    company: Company;
    year: number;
    klasa_summary: Array<{ klasa: string; naziv: string; duguje: number; potrazuje: number; saldo: number; accounts: number }>;
    accounts: Array<{ konto: string; klasa: string; naziv: string | null; duguje: number; potrazuje: number; saldo: number; cnt: number }>;
    bilanca: { aktiva: number; pasiva: number; rezultat: number };
    rdg: { prihodi_poslovni: number; fin_prihodi: number; prihodi_ukupno: number; cogs: number; opex: number; fin_rashodi: number; rashodi_ukupno: number; rezultat: number };
};

const GOLD = 'var(--ad-acc)';
const BROWN = 'var(--ad-ink)';
const eur = (n: number) => new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number.isFinite(n) ? n : 0);
const num = (n: number) => new Intl.NumberFormat('hr-HR').format(Number.isFinite(n) ? n : 0);

function statusMeta(s: Status): { color: string; bg: string; icon: string; hr: string; en: string } {
    switch (s) {
        case 'ok': return { color: '#1f7a36', bg: 'rgba(31,122,54,0.10)', icon: '✓', hr: 'Usklađeno', en: 'OK' };
        case 'warn': return { color: '#9a6b12', bg: 'rgba(154,107,18,0.12)', icon: '⚠', hr: 'Odstupanje', en: 'Warning' };
        case 'error': return { color: '#b14040', bg: 'rgba(177,64,64,0.12)', icon: '✗', hr: 'Greška', en: 'Error' };
        default: return { color: '#777', bg: 'rgba(0,0,0,0.05)', icon: '–', hr: 'N/D', en: 'N/A' };
    }
}

function HealthRing({ score, locale }: { score: number; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const r = 52; const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(100, score));
    const color = pct >= 85 ? '#1f7a36' : pct >= 60 ? '#9a6b12' : '#b14040';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <svg width={132} height={132} viewBox="0 0 132 132" role="img" aria-label={t('Ocjena zdravlja knjiga', 'Books health score')}>
                <circle cx={66} cy={66} r={r} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={12} />
                <circle cx={66} cy={66} r={r} fill="none" stroke={color} strokeWidth={12} strokeLinecap="round"
                    strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 66 66)" />
                <text x={66} y={62} textAnchor="middle" fontSize="30" fontWeight="800" fill={color}>{pct}</text>
                <text x={66} y={84} textAnchor="middle" fontSize="11" fill="#8a6817">/ 100</text>
            </svg>
            <div>
                <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ad-acc-3)' }}>{t('Ocjena ispravnosti knjiga', 'Books integrity score')}</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, color: BROWN, marginTop: 2 }}>
                    {pct >= 85 ? t('Knjige djeluju uredno', 'Books look clean') : pct >= 60 ? t('Ima odstupanja za pregled', 'Issues to review') : t('Ozbiljna odstupanja', 'Serious issues')}
                </div>
            </div>
        </div>
    );
}

function MetricsGrid({ metrics }: { metrics: Metric[] }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 10 }}>
            {metrics.map((m, i) => (
                <div key={i} style={{ background: 'rgba(var(--ad-acc-rgb),0.06)', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 8, padding: '8px 10px' }}>
                    <div className="muted" style={{ fontSize: '0.7rem' }}>{m.label}</div>
                    <div style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: BROWN, marginTop: 2 }}>{m.is_count ? num(m.value) : eur(m.value)}</div>
                </div>
            ))}
        </div>
    );
}

export function KnjigovodstvoModule({ locale, token, userEmail, onBack }: { locale: Locale; token: string | null; userEmail?: string; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const allowed = (userEmail ?? '').toLowerCase() === 'vjakomin@gmail.com';

    const [data, setData] = useState<Overview | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [companyCode, setCompanyCode] = useState('ADO');
    const [year, setYear] = useState<number | null>(null);
    const [view, setView] = useState<'board' | 'statements'>(readUrlParam('tab', 'board') as 'board' | 'statements');
    useUrlSync('tab', view, view === 'board');
    const [openControl, setOpenControl] = useState<string | null>(null);
    const [detail, setDetail] = useState<ControlDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [statements, setStatements] = useState<Statements | null>(null);
    const [stLoading, setStLoading] = useState(false);
    const [openKlasa, setOpenKlasa] = useState<string | null>(null);

    const qs = useMemo(() => {
        const p = new URLSearchParams();
        p.set('company', companyCode);
        if (year) p.set('year', String(year));
        return p.toString();
    }, [companyCode, year]);

    useEffect(() => {
        if (!allowed) { setLoading(false); return; }
        setLoading(true);
        api<Overview>(`/knjigovodstvo/overview?${qs}`, token)
            .then((r) => { setData(r); setError(''); if (!year) setYear(r.year); })
            .catch((e) => setError(e instanceof Error ? e.message : 'API'))
            .finally(() => setLoading(false));
    }, [token, qs, allowed]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!allowed || !openControl) { setDetail(null); return; }
        setDetailLoading(true);
        api<ControlDetail>(`/knjigovodstvo/control/${openControl}?${qs}`, token)
            .then((r) => setDetail(r))
            .catch(() => setDetail(null))
            .finally(() => setDetailLoading(false));
    }, [openControl, qs, token, allowed]);

    useEffect(() => {
        if (!allowed || view !== 'statements') return;
        setStLoading(true);
        api<Statements>(`/knjigovodstvo/financial-statements?${qs}`, token)
            .then((r) => setStatements(r))
            .catch(() => setStatements(null))
            .finally(() => setStLoading(false));
    }, [view, qs, token, allowed]);

    if (!allowed) {
        return (
            <section className="card admin-block">
                <h1>🔒 {t('Knjigovodstvo', 'Bookkeeping control')}</h1>
                <p className="error">{t('Ovaj modul je dostupan samo vlasniku.', 'This module is owner-only.')}</p>
            </section>
        );
    }

    const fmtDate = (s: string) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || '');
        return m ? `${m[3]}.${m[2]}.${m[1]}.` : s;
    };
    const renderCell = (col: string, v: string | number | null) => {
        if (v === null || v === undefined) return '—';
        if (typeof v === 'number') {
            const isCount = ['cnt', 'mjesec', 'accounts'].includes(col);
            return <span style={{ fontFamily: 'ui-monospace, monospace' }}>{isCount ? num(v) : eur(v)}</span>;
        }
        if (col === 'datum') return fmtDate(String(v));
        return String(v);
    };

    const activeControl = data?.controls.find((c) => c.id === openControl) ?? null;

    return (
        <section className="card admin-block">
            {onBack ? <button type="button" className="secondary compact" onClick={onBack} style={{ marginBottom: 8 }}>← {t('Natrag', 'Back')}</button> : null}

            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🧮</div>
                <div className="bi-module-hero-text">
                    <h1>10 · {t('Knjigovodstvo — kontrola', 'Bookkeeping control')}</h1>
                    <p className="muted">{t('Neovisna revizijska kontrola glavne knjige i izvornih dokumenata (HSFI / IFRS). Pokazuje gdje knjige ne štimaju.', 'Independent audit of the ledger and source documents (HSFI / IFRS). Shows where the books do not tie out.')}</p>
                </div>
            </div>

            {/* selektori + tabovi */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
                <select value={companyCode} onChange={(e) => { setCompanyCode(e.target.value); setYear(null); setStatements(null); }} className="compact">
                    <option value="ADO">ADO</option>
                </select>
                <select value={year ?? ''} onChange={(e) => setYear(Number(e.target.value))} className="compact">
                    {(data?.years ?? []).map((y) => <option key={y} value={y}>{y}.</option>)}
                </select>
                <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                    <button type="button" className={view === 'board' ? 'compact' : 'secondary compact'} onClick={() => setView('board')}>{t('Kontrolna ploča', 'Control board')}</button>
                    <button type="button" className={view === 'statements' ? 'compact' : 'secondary compact'} onClick={() => setView('statements')}>{t('Financijski izvještaji', 'Financial statements')}</button>
                </div>
            </div>

            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {error ? <p className="error">{error}</p> : null}

            {/* RAZINA 1 — kontrolna ploča */}
            {view === 'board' && data && !loading ? (
                <>
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(180deg,#fffdf6,var(--ad-bg))', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 14, padding: 16, marginBottom: 14 }}>
                        <HealthRing score={data.health_score} locale={locale} />
                        <div style={{ display: 'flex', gap: 10 }}>
                            {([['ok', data.summary.ok], ['warn', data.summary.warn], ['error', data.summary.error]] as Array<[Status, number]>).map(([s, n]) => {
                                const m = statusMeta(s);
                                return (
                                    <div key={s} style={{ textAlign: 'center', minWidth: 86, background: m.bg, border: `1px solid ${m.color}33`, borderRadius: 10, padding: '10px 14px' }}>
                                        <div style={{ fontSize: '1.6rem', fontWeight: 800, color: m.color }}>{n}</div>
                                        <div style={{ fontSize: '0.72rem', color: m.color }}>{m.icon} {locale === 'en' ? m.en : m.hr}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* cross-year alert — neravnoteža GK u bilo kojoj godini (uvijek vidljivo) */}
                    {data.cross_year_alerts.length > 0 ? (
                        <div style={{ background: 'rgba(177,64,64,0.10)', border: '1px solid #b1404055', borderLeft: '4px solid #b14040', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                            <strong style={{ color: '#b14040' }}>⚠ {t('Glavna knjiga ne balansira', 'Ledger does not balance')}</strong>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                                {data.cross_year_alerts.map((a) => (
                                    <button key={a.year} type="button" className="secondary compact" onClick={() => setYear(a.year)}
                                        style={{ borderColor: '#b1404066', color: '#b14040' }}>
                                        {a.year}.: {eur(a.diff)} {a.year === data.year ? `(${t('prikazano', 'shown')})` : `→ ${t('otvori', 'open')}`}
                                    </button>
                                ))}
                            </div>
                            <div className="muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>{t('Σ duguje ≠ Σ potražuje — temeljna povreda dvojnog knjigovodstva.', 'Σ debit ≠ Σ credit — fundamental double-entry violation.')}</div>
                        </div>
                    ) : null}

                    {/* headline nalazi */}
                    {data.headlines.length > 0 ? (
                        <div style={{ marginBottom: 16 }}>
                            {data.headlines.slice(0, 5).map((h, i) => {
                                const m = statusMeta(h.status);
                                return (
                                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 12px', borderLeft: `4px solid ${m.color}`, background: m.bg, borderRadius: 6, marginBottom: 6 }}>
                                        <span style={{ color: m.color, fontWeight: 800 }}>{m.icon}</span>
                                        <span style={{ fontSize: '0.9rem', color: '#3a2f17' }}>{locale === 'en' ? h.en : h.hr}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : <p style={{ color: '#1f7a36', fontWeight: 600 }}>✓ {t('Nema materijalnih odstupanja.', 'No material discrepancies.')}</p>}

                    {/* kontrolne kartice */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 12 }}>
                        {data.controls.map((c) => {
                            const m = statusMeta(c.status);
                            const isOpen = openControl === c.id;
                            return (
                                <div key={c.id} style={{ border: `1px solid ${isOpen ? m.color : 'rgba(var(--ad-soft-rgb),0.3)'}`, borderRadius: 12, padding: 14, background: '#fff', boxShadow: isOpen ? `0 0 0 2px ${m.color}22` : 'none' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                        <strong style={{ color: BROWN, fontSize: '0.95rem' }}>{locale === 'en' ? c.name_en : c.name_hr}</strong>
                                        <span style={{ whiteSpace: 'nowrap', color: m.color, background: m.bg, border: `1px solid ${m.color}44`, borderRadius: 20, padding: '2px 9px', fontSize: '0.74rem', fontWeight: 700 }}>{m.icon} {locale === 'en' ? m.en : m.hr}</span>
                                    </div>
                                    <div style={{ fontSize: '0.83rem', color: '#3a2f17', marginTop: 6, lineHeight: 1.4 }}>{locale === 'en' ? c.headline_en : c.headline_hr}</div>
                                    {c.metrics.length > 0 ? <MetricsGrid metrics={c.metrics} /> : null}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                                        <span className="muted" style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>📚 {c.hsfi}</span>
                                        {c.has_detail ? (
                                            <button type="button" className="secondary compact" onClick={() => { setOpenControl(isOpen ? null : c.id); }}>
                                                {isOpen ? t('Zatvori', 'Close') : t('Detalji →', 'Details →')}
                                            </button>
                                        ) : null}
                                    </div>

                                    {/* RAZINA 2 — drill-down inline */}
                                    {isOpen ? (
                                        <div style={{ marginTop: 12, borderTop: '1px dashed rgba(var(--ad-soft-rgb),0.4)', paddingTop: 10 }}>
                                            {detailLoading ? <p className="muted">{t('Učitavam detalje…', 'Loading…')}</p> : null}
                                            {detail && activeControl?.id === c.id ? (
                                                <>
                                                    <p style={{ fontSize: '0.8rem', color: '#5a4a22', background: 'rgba(var(--ad-acc-rgb),0.07)', padding: 8, borderRadius: 6 }}>{detail.explain_hr}</p>
                                                    {detail.missing_salary_months && detail.missing_salary_months.length > 0 ? <p style={{ fontSize: '0.8rem', color: '#b14040' }}>⚠ {t('Mjeseci bez plaća (kl.40):', 'Months missing payroll:')} {detail.missing_salary_months.join(', ')}</p> : null}
                                                    {detail.missing_depr_months && detail.missing_depr_months.length > 0 ? <p style={{ fontSize: '0.8rem', color: '#b14040' }}>⚠ {t('Mjeseci bez amortizacije (kl.43):', 'Months missing depreciation:')} {detail.missing_depr_months.join(', ')}</p> : null}
                                                    {detail.rows.length > 0 ? (
                                                        <div className="table-wrap" style={{ maxHeight: 320, overflow: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8 }}>
                                                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                                                                <thead><tr>{detail.columns.map((col) => <th key={col} style={{ textAlign: ['konto', 'klasa', 'naziv', 'broj', 'dokument', 'datum', 'direction', 'izvor', 'podklasa', 'kanal', 'metric', 'bucket'].includes(col) ? 'left' : 'right' }}>{col}</th>)}</tr></thead>
                                                                <tbody>
                                                                    {detail.rows.map((row, ri) => (
                                                                        <tr key={ri}>{detail.columns.map((col) => <td key={col} style={{ textAlign: typeof row[col] === 'number' && !['cnt', 'mjesec', 'accounts'].includes(col) ? 'right' : 'left' }}>{renderCell(col, row[col])}</td>)}</tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : <p className="muted">{t('Nema redaka.', 'No rows.')}</p>}
                                                </>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    <p className="muted" style={{ fontSize: '0.72rem', marginTop: 12, fontStyle: 'italic' }}>
                        {t('Generirano', 'Generated')}: {data.generated_at} · {data.company.name} · {data.year}. · {t('Read-only — modul ne mijenja knjige.', 'Read-only — does not modify the books.')}
                    </p>
                </>
            ) : null}

            {/* RAZINA 3 — financijski izvještaji */}
            {view === 'statements' ? (
                stLoading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : statements ? (
                    <>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                            <div style={{ flex: '1 1 280px', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 12, padding: 14 }}>
                                <strong style={{ color: BROWN }}>{t('Bilanca (rekonstruirana iz GK)', 'Balance sheet (reconstructed)')}</strong>
                                <table className="admin-user-table" style={{ width: '100%', fontSize: '0.86rem', marginTop: 8 }}>
                                    <tbody>
                                        <tr><td>{t('Aktiva (kl. 0,1,3,6)', 'Assets')}</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(statements.bilanca.aktiva)}</td></tr>
                                        <tr><td>{t('Pasiva (kl. 2,9)', 'Liabilities + equity')}</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(statements.bilanca.pasiva)}</td></tr>
                                        <tr><td>{t('Rezultat (RDG)', 'Result')}</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(statements.bilanca.rezultat)}</td></tr>
                                        <tr style={{ borderTop: '2px solid rgba(var(--ad-soft-rgb),0.4)' }}><td style={{ fontWeight: 700 }}>{t('Rezidual (treba 0)', 'Residual (should be 0)')}</td><td style={{ textAlign: 'right', fontWeight: 800, fontFamily: 'ui-monospace, monospace', color: Math.abs(statements.bilanca.aktiva - statements.bilanca.pasiva - statements.bilanca.rezultat) < 0.01 ? '#1f7a36' : '#b14040' }}>{eur(statements.bilanca.aktiva - statements.bilanca.pasiva - statements.bilanca.rezultat)}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ flex: '1 1 280px', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 12, padding: 14 }}>
                                <strong style={{ color: BROWN }}>{t('Račun dobiti i gubitka (RDG)', 'Income statement')}</strong>
                                <table className="admin-user-table" style={{ width: '100%', fontSize: '0.86rem', marginTop: 8 }}>
                                    <tbody>
                                        <tr><td>{t('Poslovni prihodi (75,76,78)', 'Operating revenue')}</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(statements.rdg.prihodi_poslovni)}</td></tr>
                                        <tr><td>{t('Financijski prihodi (77)', 'Financial income')}</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(statements.rdg.fin_prihodi)}</td></tr>
                                        <tr><td>COGS (71)</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: '#b14040' }}>−{eur(statements.rdg.cogs)}</td></tr>
                                        <tr><td>OPEX (40–48)</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: '#b14040' }}>−{eur(statements.rdg.opex)}</td></tr>
                                        <tr><td>{t('Financijski rashodi (47)', 'Financial expenses')}</td><td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: '#b14040' }}>−{eur(statements.rdg.fin_rashodi)}</td></tr>
                                        <tr style={{ borderTop: '2px solid rgba(var(--ad-soft-rgb),0.4)' }}><td style={{ fontWeight: 700 }}>{t('Rezultat', 'Net result')}</td><td style={{ textAlign: 'right', fontWeight: 800, fontFamily: 'ui-monospace, monospace', color: statements.rdg.rezultat >= 0 ? '#1f7a36' : '#b14040' }}>{eur(statements.rdg.rezultat)}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <strong style={{ color: BROWN, fontSize: '0.95rem' }}>{t('Bruto bilanca po klasama', 'Trial balance by class')}</strong>
                        <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, marginTop: 8 }}>
                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                <thead><tr><th>{t('Klasa', 'Class')}</th><th>{t('Naziv', 'Name')}</th><th style={{ textAlign: 'right' }}>{t('Duguje', 'Debit')}</th><th style={{ textAlign: 'right' }}>{t('Potražuje', 'Credit')}</th><th style={{ textAlign: 'right' }}>{t('Saldo', 'Balance')}</th><th style={{ textAlign: 'right' }}>{t('Konta', 'Accounts')}</th></tr></thead>
                                <tbody>
                                    {statements.klasa_summary.map((k) => (
                                        <React.Fragment key={k.klasa}>
                                            <tr style={{ cursor: 'pointer' }} onClick={() => setOpenKlasa(openKlasa === k.klasa ? null : k.klasa)}>
                                                <td style={{ fontWeight: 700 }}>{openKlasa === k.klasa ? '▾' : '▸'} {k.klasa}</td>
                                                <td>{k.naziv}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(k.duguje)}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(k.potrazuje)}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{eur(k.saldo)}</td>
                                                <td style={{ textAlign: 'right', color: 'rgba(0,0,0,0.55)' }}>{num(k.accounts)}</td>
                                            </tr>
                                            {openKlasa === k.klasa ? statements.accounts.filter((a) => a.klasa === k.klasa).map((a) => (
                                                <tr key={a.konto} style={{ background: 'rgba(var(--ad-acc-rgb),0.05)', fontSize: '0.8rem' }}>
                                                    <td></td>
                                                    <td style={{ paddingLeft: 16 }}><span style={{ fontFamily: 'ui-monospace, monospace' }}>{a.konto}</span> {a.naziv ? <span className="muted">· {a.naziv}</span> : null}</td>
                                                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(a.duguje)}</td>
                                                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(a.potrazuje)}</td>
                                                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{eur(a.saldo)}</td>
                                                    <td style={{ textAlign: 'right', color: 'rgba(0,0,0,0.45)' }}>{num(a.cnt)}</td>
                                                </tr>
                                            )) : null}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="muted" style={{ fontSize: '0.72rem', marginTop: 10, fontStyle: 'italic' }}>
                            {t('Bruto bilanca je rekonstruirana izravno iz glavne knjige (dnevnik = izvor istine). Klik na klasu otvara konta.', 'Trial balance reconstructed directly from the journal. Click a class to expand accounts.')}
                        </p>
                    </>
                ) : <p className="muted">{t('Nema podataka.', 'No data.')}</p>
            ) : null}
        </section>
    );
}
